import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { StatusTimeline } from "@/components/StatusTimeline";
import { Button } from "@/components/ui/button";
import { dateTime } from "@/lib/format";

const OPEN_STATUSES = ["requested", "confirmed", "seated"];
const CANCELLABLE = ["requested", "confirmed"];
const POLL_MS = 8000;

/** Citizen table-reservation tracking — `/reservations/:id`, derived from `/table-reservations/mine`. No payment. */
export default function ReservationDetail() {
  const { id } = useParams();
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | missing | error
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/table-reservations/mine");
      const found = rows(res.data).find((x) => String(x.id) === String(id));
      if (!found) {
        setItem(null);
        setState("missing");
        return;
      }
      setItem(found);
      setState("ready");
    } catch (e) {
      // A blip during background polling must not blank a page the citizen is already reading.
      setLoadError(apiErrorMessage(e, ""));
      setState((s) => (s === "ready" ? s : "error"));
    }
  }, [id]);

  useEffect(() => {
    setState("loading");
    load();
  }, [load]);

  // Keep asking while the thing is live: staff confirm on their side, the citizen sees it land here.
  useEffect(() => {
    if (!item || !OPEN_STATUSES.includes(item.status)) return;
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [item?.status, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/table-reservations/${id}/transition`, {
        status: "cancelled",
      });
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading")
    return (
      <div
        className="mx-auto max-w-2xl space-y-4 px-5 py-12 sm:px-8"
        data-testid="reservation-skeleton"
        aria-busy="true"
      >
        <div className="h-28 animate-pulse rounded-2xl bg-muted" />
        <div className="h-40 animate-pulse rounded-2xl bg-muted" />
      </div>
    );
  if (state === "missing")
    return (
      <div
        className="mx-auto max-w-2xl px-5 py-24 text-center"
        data-testid="reservation-not-found"
      >
        <p className="text-lg">{t("tables.notFound")}</p>
        <Button
          className="mt-6 rounded-full"
          onClick={() => navigate("/activity")}
        >
          {t("tables.backToActivity")}
        </Button>
      </div>
    );
  if (state === "error")
    return (
      <div
        className="mx-auto max-w-2xl px-5 py-24 text-center"
        data-testid="reservation-error"
      >
        <p className="text-lg text-destructive">
          {loadError || t("tables.loadFailed")}
        </p>
        <Button
          className="mt-6 rounded-full"
          onClick={() => {
            setState("loading");
            load();
          }}
          data-testid="reservation-retry-btn"
        >
          {t("common.retry")}
        </Button>
      </div>
    );

  return (
    <div
      className="mx-auto max-w-2xl px-5 py-12 sm:px-8"
      data-testid="reservation-page"
    >
      <div className="rounded-2xl bg-[hsl(var(--primary))] p-6 text-primary-foreground sm:p-8">
        <p className="text-sm opacity-80">{t("tables.detailTitle")}</p>
        <h1
          className="mt-1 font-heading text-3xl font-bold tracking-tight"
          data-testid="reservation-headline"
        >
          {t("tables.statusLabels")[item.status] || item.status}
        </h1>
      </div>

      <div className="mt-6 space-y-6">
        <div className="rounded-2xl border border-border p-5">
          <p
            className="font-heading text-lg font-semibold"
            data-testid="reservation-title"
          >
            {item.business_name}
          </p>
          <StatusTimeline
            timeline={item.timeline}
            labelsKey="tables.statusLabels"
            testId="reservation-timeline"
          />
        </div>

        <div className="rounded-2xl border border-border p-5 text-sm">
          <Row
            label={t("tables.slotLabel")}
            value={dateTime(item.slot_start_at, lang)}
          />
          <Row label={t("tables.partySize")} value={item.party_size} />
          <Row
            label={t("tables.guestName")}
            value={item.guest_snapshot?.name}
          />
          {item.notes && <Row label={t("tables.notes")} value={item.notes} />}
        </div>

        {error && (
          <p
            className="text-sm text-destructive"
            data-testid="reservation-error-msg"
          >
            {error}
          </p>
        )}

        {CANCELLABLE.includes(item.status) && (
          <Button
            variant="outline"
            className="rounded-full"
            disabled={busy}
            onClick={cancel}
            data-testid="reservation-cancel-btn"
          >
            {t("common.cancel")}
          </Button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-right">{value}</span>
    </div>
  );
}
